/**
 * Overview:
 * This Redux slice manages the application's authentication state.
 * It stores user account information, authentication status, and
 * assigned roles, while providing actions and selectors for handling
 * sign-in, sign-out, and access control throughout the application.
 */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { Account } from "../auth/types";

export type AuthStatus = "initializing" | "signedOut" | "pending" | "mfa" | "signedIn";

type AuthState = {
  status: AuthStatus;
  account: Account | null;
  roles: string[];
};

const initialState: AuthState = {
  status: "initializing",
  account: null,
  roles: [],
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setStatus(state, action: PayloadAction<AuthStatus>) {
      state.status = action.payload;
    },
    setAccount(state, action: PayloadAction<Account | null>) {
      state.account = action.payload;
    },
    setRoles(state, action: PayloadAction<string[]>) {
      state.roles = action.payload;
    },
    clearAuth(state) {
      state.status = "signedOut";
      state.account = null;
      state.roles = [];
    },
  },
});

export const { setStatus, setAccount, setRoles, clearAuth } = authSlice.actions;

export const selectAuthStatus = (state: any) => state?.auth?.status ?? "initializing";
export const selectAuthAccount = (state: any) => state?.auth?.account ?? null;
export const selectAuthRoles = (state: any) => state?.auth?.roles ?? [];
export const selectIsAuthenticated = (state: any) => state.auth.status === "signedIn";

export default authSlice.reducer;
